import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useSeoConfigurations, SeoConfiguration } from '@/hooks/useSeoConfigurations';
import AdminLayout from '@/components/admin/AdminLayout';
import { Pencil, Plus, Trash2, Globe, Search, Image, Tag, User, Copyright } from 'lucide-react';

export const AdminSeoConfigurations = () => {
  const { seoConfigurations, loading, createSeoConfiguration, updateSeoConfiguration, deleteSeoConfiguration } = useSeoConfigurations();
  const [selectedConfig, setSelectedConfig] = useState<SeoConfiguration | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [formData, setFormData] = useState({
    page_path: '',
    page_name: '',
    title: '',
    description: '',
    image_url: '',
    canonical_url: '',
    keywords: '',
    og_type: 'website',
    og_title: '',
    og_url: '',
    og_image: '',
    og_image_alt: '',
    og_description: '',
    og_site_name: '',
    author: '',
    copyright: '',
    is_active: true,
  });

  const resetForm = () => {
    setFormData({
      page_path: '',
      page_name: '',
      title: '',
      description: '',
      image_url: '',
      canonical_url: '',
      keywords: '',
      og_type: 'website',
      og_title: '',
      og_url: '',
      og_image: '',
      og_image_alt: '',
      og_description: '',
      og_site_name: '',
      author: '',
      copyright: '',
      is_active: true,
    });
    setSelectedConfig(null);
    setIsEditing(false);
  };

  const handleOpenDialog = (config?: SeoConfiguration) => {
    if (config) {
      setFormData({
        page_path: config.page_path,
        page_name: config.page_name,
        title: config.title || '',
        description: config.description || '',
        image_url: config.image_url || '',
        canonical_url: config.canonical_url || '',
        keywords: config.keywords || '',
        og_type: config.og_type || 'website',
        og_title: config.og_title || '',
        og_url: config.og_url || '',
        og_image: config.og_image || '',
        og_image_alt: config.og_image_alt || '',
        og_description: config.og_description || '',
        og_site_name: config.og_site_name || '',
        author: config.author || '',
        copyright: config.copyright || '',
        is_active: config.is_active,
      });
      setSelectedConfig(config);
      setIsEditing(true);
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isEditing && selectedConfig) {
        await updateSeoConfiguration(selectedConfig.id, formData);
      } else {
        await createSeoConfiguration(formData);
      }
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving SEO configuration:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this SEO configuration?')) {
      await deleteSeoConfiguration(id);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-lg">Loading SEO configurations...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">SEO Configurations</h1>
            <p className="text-muted-foreground">Manage SEO settings for each page</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => handleOpenDialog()}>
                <Plus className="mr-2 h-4 w-4" />
                Add SEO Configuration
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{isEditing ? 'Edit SEO Configuration' : 'Add SEO Configuration'}</DialogTitle>
                <DialogDescription>
                  Configure SEO metadata for your pages
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <Tabs defaultValue="basic" className="w-full">
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="basic" className="flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      Basic
                    </TabsTrigger>
                    <TabsTrigger value="seo" className="flex items-center gap-2">
                      <Search className="h-4 w-4" />
                      SEO
                    </TabsTrigger>
                    <TabsTrigger value="opengraph" className="flex items-center gap-2">
                      <Image className="h-4 w-4" />
                      Open Graph
                    </TabsTrigger>
                    <TabsTrigger value="meta" className="flex items-center gap-2">
                      <Tag className="h-4 w-4" />
                      Meta
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="basic" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="page_path">Page Path *</Label>
                        <Input
                          id="page_path"
                          value={formData.page_path}
                          onChange={(e) => handleInputChange('page_path', e.target.value)}
                          placeholder="/about"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="page_name">Page Name *</Label>
                        <Input
                          id="page_name"
                          value={formData.page_name}
                          onChange={(e) => handleInputChange('page_name', e.target.value)}
                          placeholder="About Us"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="title">Title</Label>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        placeholder="Page Title - Site Name"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="is_active"
                        checked={formData.is_active}
                        onCheckedChange={(checked) => handleInputChange('is_active', checked)}
                      />
                      <Label htmlFor="is_active">Active</Label>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="seo" className="space-y-4">
                    <div>
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        placeholder="Brief description of the page content"
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label htmlFor="keywords">Keywords</Label>
                      <Input
                        id="keywords"
                        value={formData.keywords}
                        onChange={(e) => handleInputChange('keywords', e.target.value)}
                        placeholder="keyword1, keyword2, keyword3"
                      />
                    </div>
                    <div>
                      <Label htmlFor="canonical_url">Canonical URL</Label>
                      <Input
                        id="canonical_url"
                        value={formData.canonical_url}
                        onChange={(e) => handleInputChange('canonical_url', e.target.value)}
                        placeholder="https://example.com/page"
                      />
                    </div>
                    <div>
                      <Label htmlFor="image_url">Image URL</Label>
                      <Input
                        id="image_url"
                        value={formData.image_url}
                        onChange={(e) => handleInputChange('image_url', e.target.value)}
                        placeholder="https://example.com/image.jpg"
                      />
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="opengraph" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="og_type">OG Type</Label>
                        <Input
                          id="og_type"
                          value={formData.og_type}
                          onChange={(e) => handleInputChange('og_type', e.target.value)}
                          placeholder="website"
                        />
                      </div>
                      <div>
                        <Label htmlFor="og_site_name">OG Site Name</Label>
                        <Input
                          id="og_site_name"
                          value={formData.og_site_name}
                          onChange={(e) => handleInputChange('og_site_name', e.target.value)}
                          placeholder="Your Site Name"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="og_title">OG Title</Label>
                      <Input
                        id="og_title"
                        value={formData.og_title}
                        onChange={(e) => handleInputChange('og_title', e.target.value)}
                        placeholder="Open Graph Title"
                      />
                    </div>
                    <div>
                      <Label htmlFor="og_description">OG Description</Label>
                      <Textarea
                        id="og_description"
                        value={formData.og_description}
                        onChange={(e) => handleInputChange('og_description', e.target.value)}
                        placeholder="Description for social media sharing"
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label htmlFor="og_url">OG URL</Label>
                      <Input
                        id="og_url"
                        value={formData.og_url}
                        onChange={(e) => handleInputChange('og_url', e.target.value)}
                        placeholder="https://example.com/page"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="og_image">OG Image</Label>
                        <Input
                          id="og_image"
                          value={formData.og_image}
                          onChange={(e) => handleInputChange('og_image', e.target.value)}
                          placeholder="https://example.com/og-image.jpg"
                        />
                      </div>
                      <div>
                        <Label htmlFor="og_image_alt">OG Image Alt</Label>
                        <Input
                          id="og_image_alt"
                          value={formData.og_image_alt}
                          onChange={(e) => handleInputChange('og_image_alt', e.target.value)}
                          placeholder="Alt text for OG image"
                        />
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="meta" className="space-y-4">
                    <div>
                      <Label htmlFor="author">Author</Label>
                      <Input
                        id="author"
                        value={formData.author}
                        onChange={(e) => handleInputChange('author', e.target.value)}
                        placeholder="Author Name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="copyright">Copyright</Label>
                      <Input
                        id="copyright"
                        value={formData.copyright}
                        onChange={(e) => handleInputChange('copyright', e.target.value)}
                        placeholder="© 2024 Company Name"
                      />
                    </div>
                  </TabsContent>
                </Tabs>
                
                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">
                    {isEditing ? 'Update' : 'Create'} Configuration
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid gap-6">
          {seoConfigurations.map((config) => (
            <Card key={config.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="h-5 w-5" />
                      {config.page_name}
                      <Badge variant={config.is_active ? "default" : "secondary"}>
                        {config.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </CardTitle>
                    <CardDescription>
                      Path: <code className="bg-muted px-2 py-1 rounded text-sm">{config.page_path}</code>
                    </CardDescription>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenDialog(config)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(config.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {config.title && (
                    <div>
                      <Label className="text-sm font-medium">Title</Label>
                      <p className="text-sm text-muted-foreground">{config.title}</p>
                    </div>
                  )}
                  {config.description && (
                    <div>
                      <Label className="text-sm font-medium">Description</Label>
                      <p className="text-sm text-muted-foreground line-clamp-2">{config.description}</p>
                    </div>
                  )}
                  {config.keywords && (
                    <div>
                      <Label className="text-sm font-medium">Keywords</Label>
                      <p className="text-sm text-muted-foreground">{config.keywords}</p>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 mt-4">
                    {config.og_title && <Badge variant="outline">OG Title</Badge>}
                    {config.og_image && <Badge variant="outline">OG Image</Badge>}
                    {config.canonical_url && <Badge variant="outline">Canonical</Badge>}
                    {config.author && <Badge variant="outline">Author</Badge>}
                    {config.copyright && <Badge variant="outline">Copyright</Badge>}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {seoConfigurations.length === 0 && (
          <div className="text-center py-12">
            <Globe className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-2 text-sm font-medium">No SEO configurations</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Get started by creating a new SEO configuration.
            </p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};